import React from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, Button, Box, DialogProps } from "@mui/material";
import { IconType } from "react-icons";

interface CustomAlertProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string | React.ReactNode;
  subMessage?: string;
  icon?: IconType | React.ReactNode;
  iconColor?: string;
  messageAlignment?: "center" | "left";
  messageClassName?: string;
  contentStyle?: React.CSSProperties;
  dialogProps?: Partial<DialogProps>;
  width?: string | number;
  height?: string | number;
  buttons?:
    | Array<{
        label: string;
        onClick: () => void;
        color: "primary" | "success" | "error" | "info" | "warning" | "inherit";
        variant?: "contained" | "outlined";
      }>
    | React.ReactNode;
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  subMessage,
  icon: Icon,
  iconColor = "#3f51b5",
  messageAlignment = "center",
  messageClassName = "",
  contentStyle = {},
  dialogProps,
  width = "40vw",
  height = "55vh",
  buttons = [
    { label: "Cancelar", onClick: onClose, color: "error", variant: "outlined" },
    { label: "Confirmar", onClick: onConfirm, color: "success", variant: "contained" },
  ],
}) => {
  const processedWidth = typeof width === "number" ? `${width}px` : width;
  const processedHeight = typeof height === "number" ? `${height}px` : height;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      {...dialogProps}
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "90vw", sm: "60vw", md: "40vw" },
          maxHeight: "80vh",
          paddingTop: "2vh",
          paddingBottom: "2vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        },
        ...dialogProps?.sx,
      }}
    >
      {Icon && (
        <Box display="flex" justifyContent="center" mt={2} mb={3}>
          {typeof Icon === "function" ? <Icon style={{ fontSize: "5vh", color: iconColor }} /> : Icon}
        </Box>
      )}
      <DialogTitle style={{ textAlign: "center", fontWeight: "bold" }}>{title}</DialogTitle>
      <DialogContent
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          ...contentStyle,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "80%" }}>
          <Typography
            variant="body2"
            color="textSecondary"
            fontSize={{ xs: "3vw", sm: "1.5vw", md: "1vw" }}
            align={messageAlignment}
            className={messageClassName}
          >
            {message}
          </Typography>
          {subMessage && (
            <Typography
              variant="body2"
              color="textSecondary"
              fontSize={{ xs: "3vw", sm: "1.5vw", md: "1vw" }}
              align={messageAlignment}
              sx={{ marginTop: 2, whiteSpace: "pre-line" }}
              className={messageClassName}
            >
              {subMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ justifyContent: "center", marginBottom: "3vh" }}>
        {Array.isArray(buttons)
          ? buttons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                variant={button.variant || "contained"}
                color={button.color || "inherit"}
                sx={{ width: "15vw" }}
              >
                {button.label}
              </Button>
            ))
          : buttons}
      </DialogActions>
    </Dialog>
  );
};

export default CustomAlert;
